import React, { useMemo, useState } from 'react';
import type { PnidDetail } from '../../types/adminDashboard';

interface Props {
  data: PnidDetail[] | null;
  onOpenDocument?: (docId: string) => void;
}

type SortKey = 'plant' | 'pnid_no' | 'pnid_title' | 'status_label' | 'user' | 'updated_at';
type SortDir = 'asc' | 'desc';

interface StatusConfig {
  label: string;
  bg: string;
  color: string;
  border: string;
}

const STATUS_ORDER = ['todo', 'in_progress', 'needs_review', 'completed'];

const STATUS_CONFIG: Record<string, StatusConfig> = {
  todo: { label: 'Todo', bg: '#f8fafc', color: '#475569', border: '#cbd5e1' },
  in_progress: { label: 'In Progress', bg: '#fffbeb', color: '#92400e', border: '#fcd34d' },
  needs_review: { label: 'Needs Review', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  completed: { label: 'Completed', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
};

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'plant', label: 'Plant' },
  { key: 'pnid_no', label: 'P&ID No' },
  { key: 'pnid_title', label: 'Title' },
  { key: 'status_label', label: 'Status' },
  { key: 'user', label: 'Owner' },
  { key: 'updated_at', label: 'Updated' },
];

export default function PlantPnidTable({ data, onOpenDocument }: Props) {
  const [activePlant, setActivePlant] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updated_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = data ?? [];

  const plants = useMemo(() => {
    const set = new Set(rows.map((row) => row.plant).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const selectedPlant = activePlant === 'all' || plants.includes(activePlant) ? activePlant : 'all';

  const plantScopedRows = useMemo(() => {
    if (selectedPlant === 'all') return rows;
    return rows.filter((row) => row.plant === selectedPlant);
  }, [rows, selectedPlant]);

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of plantScopedRows) {
      const key = getStatusKey(row);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [plantScopedRows]);

  const statusOptions = useMemo(() => {
    const keys = Array.from(statusCounts.keys()).sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a);
      const bi = STATUS_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return ['all', ...keys];
  }, [statusCounts]);

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    const activeStatusKey = activeStatus;

    return plantScopedRows
      .filter((row) => activeStatusKey === 'all' || getStatusKey(row) === activeStatusKey)
      .filter((row) => {
        if (!q) return true;
        return [
          row.plant,
          row.pnid_no,
          row.pnid_title,
          row.status_label,
          row.status,
          row.user,
        ].some((value) => normalizeText(value).includes(q));
      })
      .sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [plantScopedRows, activeStatus, search, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'updated_at' ? 'desc' : 'asc');
  };

  if (!data || data.length === 0) {
    return (
      <section style={sectionStyle}>
        <Header total={0} visible={0} />
        <div style={emptyStyle}>No P&ID data available</div>
      </section>
    );
  }

  return (
    <section style={sectionStyle}>
      <Header total={plantScopedRows.length} visible={filtered.length} />

      <div style={controlPanelStyle}>
        <div style={plantTabsStyle} role="tablist" aria-label="Plant filter">
          <button
            type="button"
            onClick={() => setActivePlant('all')}
            style={selectedPlant === 'all' ? activeFilterButtonStyle : filterButtonStyle}
          >
            All plants
            <span style={countPillStyle}>{rows.length}</span>
          </button>
          {plants.map((plant) => (
            <button
              key={plant}
              type="button"
              onClick={() => setActivePlant(plant)}
              style={selectedPlant === plant ? activeFilterButtonStyle : filterButtonStyle}
            >
              {plant}
              <span style={countPillStyle}>{rows.filter((row) => row.plant === plant).length}</span>
            </button>
          ))}
        </div>

        <div style={toolbarStyle}>
          <div style={statusFiltersStyle} role="tablist" aria-label="Status filter">
            {statusOptions.map((status) => {
              const label = status === 'all' ? 'All' : getStatusConfig(status).label;
              const count = status === 'all' ? plantScopedRows.length : statusCounts.get(status) ?? 0;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => setActiveStatus(status)}
                  style={activeStatus === status ? activeStatusButtonStyle : statusButtonStyle}
                >
                  {label}
                  <span style={countPillStyle}>{count}</span>
                </button>
              );
            })}
          </div>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search number, title, owner"
            style={searchStyle}
            aria-label="Search P&ID queue"
          />
        </div>
      </div>

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column.key} style={thStyle}>
                  <button
                    type="button"
                    onClick={() => handleSort(column.key)}
                    style={sortButtonStyle}
                  >
                    {column.label}
                    {sortKey === column.key && (
                      <span style={sortIndicatorStyle}>{sortDir === 'asc' ? 'Asc' : 'Desc'}</span>
                    )}
                  </button>
                </th>
              ))}
              <th style={actionThStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td style={emptyRowStyle} colSpan={COLUMNS.length + 1}>
                  No diagrams match the current filters
                </td>
              </tr>
            ) : (
              filtered.map((row) => {
                const statusKey = getStatusKey(row);
                const status = getStatusConfig(statusKey);
                return (
                  <tr key={row.id} style={trStyle}>
                    <td style={tdStyle}>{row.plant || '-'}</td>
                    <td style={monoTdStyle}>{row.pnid_no || '-'}</td>
                    <td style={titleTdStyle}>{row.pnid_title || '-'}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          ...badgeStyle,
                          backgroundColor: status.bg,
                          color: status.color,
                          borderColor: status.border,
                        }}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td style={tdStyle}>{row.user || 'Unassigned'}</td>
                    <td style={tdStyle}>{formatDate(row.updated_at)}</td>
                    <td style={actionTdStyle}>
                      <button
                        type="button"
                        onClick={() => onOpenDocument?.(row.id)}
                        style={openButtonStyle}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Header({ total, visible }: { total: number; visible: number }) {
  return (
    <div style={headerStyle}>
      <div>
        <h2 style={titleStyle}>P&ID queue</h2>
        <p style={subtitleStyle}>All active diagrams, owners, status, and latest movement.</p>
      </div>
      <span style={rowCountStyle}>{visible} of {total}</span>
    </div>
  );
}

function normalizeText(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function getStatusKey(row: PnidDetail): string {
  const raw = normalizeText(row.status || row.status_label).replace(/\s+/g, '_');
  if (raw.includes('complete') || raw.includes('reviewed')) return 'completed';
  if (raw.includes('review')) return 'needs_review';
  if (raw.includes('progress')) return 'in_progress';
  if (raw.includes('draft') || raw.includes('todo') || !raw) return 'todo';
  return raw;
}

function getStatusConfig(status: string): StatusConfig {
  return STATUS_CONFIG[status] ?? {
    label: status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
    bg: '#f8fafc',
    color: '#475569',
    border: '#cbd5e1',
  };
}

function compareRows(a: PnidDetail, b: PnidDetail, sortKey: SortKey, sortDir: SortDir): number {
  if (sortKey === 'updated_at') {
    const av = new Date(a.updated_at || 0).getTime();
    const bv = new Date(b.updated_at || 0).getTime();
    const cmp = av === bv ? 0 : av > bv ? 1 : -1;
    return sortDir === 'asc' ? cmp : -cmp;
  }

  const av = String(a[sortKey] ?? '');
  const bv = String(b[sortKey] ?? '');
  const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
  return sortDir === 'asc' ? cmp : -cmp;
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '18px 20px',
  borderBottom: '1px solid #e2e8f0',
  flexWrap: 'wrap',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 17,
  lineHeight: 1.25,
  fontWeight: 750,
  color: '#0f172a',
};

const subtitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  color: '#64748b',
};

const rowCountStyle: React.CSSProperties = {
  padding: '5px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 750,
  color: '#334155',
  backgroundColor: '#f8fafc',
};

const controlPanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '14px 20px',
  borderBottom: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
};

const plantTabsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  overflowX: 'auto',
  paddingBottom: 2,
};

const filterButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid #cbd5e1',
  borderRadius: 999,
  padding: '7px 11px',
  color: '#475569',
  backgroundColor: '#fff',
  fontSize: 12,
  fontWeight: 750,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const activeFilterButtonStyle: React.CSSProperties = {
  ...filterButtonStyle,
  borderColor: '#2563eb',
  color: '#1d4ed8',
  backgroundColor: '#eff6ff',
};

const countPillStyle: React.CSSProperties = {
  minWidth: 20,
  padding: '1px 6px',
  borderRadius: 999,
  backgroundColor: 'rgba(15, 23, 42, 0.08)',
  fontSize: 11,
  lineHeight: 1.4,
  textAlign: 'center',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const statusFiltersStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  overflowX: 'auto',
  paddingBottom: 2,
};

const statusButtonStyle: React.CSSProperties = {
  ...filterButtonStyle,
  borderRadius: 7,
  padding: '6px 10px',
};

const activeStatusButtonStyle: React.CSSProperties = {
  ...statusButtonStyle,
  borderColor: '#0f172a',
  color: '#0f172a',
  backgroundColor: '#fff',
};

const searchStyle: React.CSSProperties = {
  width: 'min(320px, 100%)',
  minHeight: 34,
  padding: '7px 11px',
  borderRadius: 7,
  border: '1px solid #cbd5e1',
  color: '#0f172a',
  fontSize: 13,
  outline: 'none',
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: 'auto',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 980,
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  textAlign: 'left',
};

const actionThStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: 'right',
};

const sortButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  cursor: 'pointer',
};

const sortIndicatorStyle: React.CSSProperties = {
  padding: '1px 5px',
  borderRadius: 999,
  backgroundColor: '#e2e8f0',
  color: '#334155',
  fontSize: 10,
  letterSpacing: 0,
  textTransform: 'none',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid #f1f5f9',
};

const tdStyle: React.CSSProperties = {
  padding: '11px 12px',
  color: '#334155',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

const monoTdStyle: React.CSSProperties = {
  ...tdStyle,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  color: '#0f172a',
};

const titleTdStyle: React.CSSProperties = {
  ...tdStyle,
  maxWidth: 360,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionTdStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: '1px solid',
  borderRadius: 999,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 750,
  whiteSpace: 'nowrap',
};

const openButtonStyle: React.CSSProperties = {
  border: '1px solid #cbd5e1',
  borderRadius: 7,
  backgroundColor: '#fff',
  color: '#1d4ed8',
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 750,
  cursor: 'pointer',
};

const emptyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 140,
  color: '#94a3b8',
  fontSize: 14,
};

const emptyRowStyle: React.CSSProperties = {
  ...emptyStyle,
  display: 'table-cell',
  textAlign: 'center',
  padding: 36,
};
